import { useState } from 'react'
import classNames from 'classnames'

import { EMPTY_OBJECT } from '../../../constants/common'
import { useInfiniteItemsListLoader, useRegisterReload } from '../../../hooks'
import { getQaSelector } from '../../../utils/common'
import { getVisibleFilters } from '../../../utils/filter'
import Button from '../../Button'
import { FILTER_BAR_FILTER_TRIGGER_VARIANT, FilterBar } from '../../Filter'
import { FilterDivider } from '../../FilterDivider'
import { NewsboardList, NewsboardPost } from '../../Newsboard'
import { getEmptyStateActions } from '../../PageEmptyState'
import {
  checkPostListIsLastPage,
  getPostListItemsData,
  getPosts,
  PostListNoData,
} from '../../Post'

import {
  ENTITY_TAB_POSTS_DEFAULT_QA_NAME,
  ENTITY_TAB_POSTS_EXCLUDE_FILTERS,
  ENTITY_TAB_POSTS_FILTER_SUB_MENU,
} from './EntityTabPosts.constants'
import {
  useEntityTabPostsFilters,
  useEntityTabPostsListParams,
} from './EntityTabPosts.hooks'
import {
  getEntityTabPostsFilterBarPermanentFilters,
  parseEntityTabPostsFilterBarPermanentFilters,
} from './EntityTabPosts.utils'
import { EntityTabPostsSearch } from './EntityTabPostsSearch'
import { EntityTabPostsToolbar } from './EntityTabPostsToolbar'

import * as styles from './EntityTabPosts.module.css'

import PlusIcon from '../../../icons/plus.svg'

function EntityTabPosts({
  addNewQaName,
  addNewText,
  canCreate = false,
  canSearch = false,
  filterEntity,
  qaName = ENTITY_TAB_POSTS_DEFAULT_QA_NAME,
  scopeFilterKey,
  scopeId,
  onCreate,
  onRegisterReload,
}) {
  const {
    availableFilters,
    filterValues,
    handleSetFilterValues,
    hasFilterChanged,
  } = useEntityTabPostsFilters({ filterEntity, scopeId })

  const {
    apiParams,
    debouncedSearch,
    getNextApiParams,
    handleChangeSearch,
    handleClearSearch,
    handleSelectOrdering,
    orderingValue,
    search,
  } = useEntityTabPostsListParams({ filterValues, scopeFilterKey, scopeId })

  const [permissions, setPermissions] = useState(EMPTY_OBJECT)

  const {
    getThresholdItemProps,
    items,
    loadingInitial,
    loadingMore,
    updateListData,
  } = useInfiniteItemsListLoader({
    apiParams,
    checkIsLastPage: checkPostListIsLastPage,
    getApiParamsLoadNext: getNextApiParams,
    getData: getPosts,
    getDataItems: getPostListItemsData,
    loadPermission: !!scopeId && (!filterEntity || filterValues !== null),
    onChangePermissions: setPermissions,
  })

  const [itemsExist, setItemsExist] = useState(false)

  // Capture, once, that items have appeared — during render — so the search
  // box stays mounted even when a query returns nothing.
  if (!itemsExist && items?.length > 0) {
    setItemsExist(true)
  }

  useRegisterReload({
    isReady: !loadingInitial,
    reload: updateListData,
    onRegisterReload,
  })

  const loading = loadingInitial || loadingMore

  const showEmptyState = !loading && items?.length === 0

  const showSearch = canSearch && (itemsExist || !!debouncedSearch)

  const showFilters =
    !!filterEntity &&
    getVisibleFilters(availableFilters).length > 0 &&
    (itemsExist || hasFilterChanged)

  const showCreate = canCreate && permissions?.can_create

  const searchNode = showSearch ? (
    <EntityTabPostsSearch
      value={search}
      onChange={handleChangeSearch}
      onClear={handleClearSearch}
    />
  ) : null

  const filterDividerNode = showSearch && showFilters ? <FilterDivider /> : null

  const filterBarNode = showFilters ? (
    <FilterBar
      responsive
      availableFilters={availableFilters}
      buttonsClassName={styles.filterBarButtons}
      className={styles.filterBar}
      entity={filterEntity}
      excludeFilters={ENTITY_TAB_POSTS_EXCLUDE_FILTERS}
      filterTriggerVariant={FILTER_BAR_FILTER_TRIGGER_VARIANT.COMPACT_ICON}
      filterValues={filterValues}
      getPermanentFilters={getEntityTabPostsFilterBarPermanentFilters}
      parsePermanentFilters={parseEntityTabPostsFilterBarPermanentFilters}
      setFilterValues={handleSetFilterValues}
      singleFieldApiParams={{ [scopeFilterKey]: scopeId }}
      sub={ENTITY_TAB_POSTS_FILTER_SUB_MENU}
    />
  ) : null

  function handleCreate() {
    onCreate?.({ onReloadList: updateListData })
  }

  return (
    <div
      {...getQaSelector({ value: qaName })}
      className={classNames(styles.container, {
        [styles.containerEmpty]: showEmptyState,
      })}
    >
      {showEmptyState ? (
        <>
          {showSearch || showFilters ? (
            <div className={styles.header}>
              {searchNode}
              {filterDividerNode}
              {filterBarNode}
            </div>
          ) : null}
          <PostListNoData
            actions={getEmptyStateActions({
              actionName: addNewQaName,
              qaName: addNewQaName,
              shouldShowActions:
                showCreate && !debouncedSearch && !hasFilterChanged,
              text: addNewText,
              onClick: handleCreate,
            })}
          />
        </>
      ) : (
        <>
          {showSearch || showFilters || showCreate ? (
            <div className={styles.header}>
              {searchNode}
              {filterDividerNode}
              {filterBarNode}
              {showCreate ? (
                <Button.Text
                  beforeIconProps={{ Icon: PlusIcon }}
                  className={styles.addNew}
                  qaName={addNewQaName}
                  text={addNewText}
                  onClick={handleCreate}
                />
              ) : null}
            </div>
          ) : null}
          <EntityTabPostsToolbar
            selectedOrderingValue={orderingValue}
            onSelectOrdering={handleSelectOrdering}
          />
          <NewsboardList>
            {items?.map((item, index) => (
              <NewsboardPost
                {...getThresholdItemProps(index)}
                disableReadTracking
                fullWidth
                key={item.uuid || index}
                value={item}
                onReload={updateListData}
              />
            ))}
            {loading ? <NewsboardPost fullWidth loading /> : null}
          </NewsboardList>
        </>
      )}
    </div>
  )
}

export default EntityTabPosts
